import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { recruitmentCompanyService } from '../../services/recruitmentCompanyService';
import { authService } from '../../services/authService';
import RecruitmentCompanyNav from '../../components/RecruitmentCompanyNav';
import RecruitmentCompanyBottomNavigation from '../../components/RecruitmentCompanyBottomNavigation';
import Loader from '../../components/Loader';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../hooks/useTranslation';
import { STORAGE_BASE_URL } from '../../config/api';

const RecruitmentCompanyEditProfile = () => {
  const user = authService.getCurrentUser();
  const { t, locale } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '',
    bio: '',
    address: '',
    site_link: '',
    email: '',
  });
  const [imageFile, setImageFile] = useState(null);
  const [currentImage, setCurrentImage] = useState(null);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const data = await recruitmentCompanyService.getProfile();
      const company = data?.data ?? data;
      setForm({
        name: company?.name ?? '',
        bio: company?.bio ?? '',
        address: company?.address ?? '',
        site_link: company?.site_link ?? '',
        email: company?.email ?? '',
      });
      setCurrentImage(company?.image ?? null);
    } catch (error) {
      const message = error?.response?.data?.message || t('company.profile.loadFailed') || 'فشل تحميل الملف الشخصي';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProfile();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('يرجى رفع ملف صورة');
        return;
      }
      if (file.size > 2 * 1024 * 1024) { // 2MB limit
        toast.error('حجم الصورة يجب ألا يتجاوز 2MB');
        return;
      }
      setImageFile(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setCurrentImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const formData = new FormData();
      formData.append('name', form.name);
      formData.append('bio', form.bio || '');
      formData.append('address', form.address || '');
      formData.append('site_link', form.site_link || '');
      formData.append('email', form.email || '');

      if (imageFile) {
        formData.append('image', imageFile);
      }

      await recruitmentCompanyService.updateProfile(formData);
      toast.success(t('company.editProfile.saveSuccess') || 'تم حفظ التغييرات بنجاح');
      // Reload profile to get updated image URL
      await loadProfile();
      setImageFile(null);
      navigate('/recruitment-company/profile');
    } catch (error) {
      const message = error?.response?.data?.message || t('company.editProfile.saveFailed') || 'فشل حفظ التغييرات';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (loading) {
    return <Loader fullScreen={true} size="large" />;
  }

  const imageUrl = currentImage
    ? (currentImage.startsWith('http') || currentImage.startsWith('data:')
        ? currentImage
        : `${STORAGE_BASE_URL}/${currentImage}`)
    : null;

  return (
    <div className="employee-home-page" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
      {/* <RecruitmentCompanyNav user={user} /> */}

      {/* Header */}
      <div className="settings-header d-flex justify-content-between align-items-center px-3 py-3" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
        <div className="notification-icon">
          <span className="bell-icon">🔔</span>
        </div>
        <div className="settings-title">{t('company.editProfile.title') || 'تعديل الملف الشخصي'}</div>
        <button className="settings-back-btn" onClick={handleBack}>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#343330" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points={locale === 'ar' ? "15 18 9 12 15 6" : "9 18 15 12 9 6"}></polyline>
          </svg>
        </button>
      </div>

      <div className="all-jobs-section px-3 mb-5">
        {/* Creative header banner */}
        <div className="edit-hero mb-3">
          <div className="edit-hero-inner">
            <div className="edit-hero-badge">⚙️</div>
            <div className="edit-hero-text">
              <div className="edit-hero-title">{t('company.editProfile.title') || 'تعديل الملف الشخصي'}</div>
              <div className="edit-hero-subtitle">{t('company.editProfile.subtitle') || 'احتفظ بمعلوماتك محدثة للتواصل السهل'}</div>
            </div>
          </div>
        </div>

        {/* Modern edit card */}
        <div className="edit-card mb-4">
          <form onSubmit={handleSubmit} encType="multipart/form-data">
            {/* Image Upload Section */}
            <div className="form-group-modern grid-span-2 mb-4">
              <label className="form-label">{t('company.editProfile.image') || 'صورة الشركة'}</label>

              {/* Current Image Display with Change Option */}
              {imageUrl ? (
                <div style={{ position: 'relative' }}>
                  <div className="image-preview-card" style={{
                    background: 'linear-gradient(135deg, #f0f7ff 0%, #e8f2ff 100%)',
                    border: '2px dashed #4a7bc4',
                    borderRadius: '12px',
                    padding: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative'
                  }}>
                    <img
                      src={imageUrl}
                      alt="Company"
                      style={{
                        maxWidth: '200px',
                        maxHeight: '200px',
                        borderRadius: '12px',
                        objectFit: 'cover'
                      }}
                    />
                    <input
                      id="image-upload-input"
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      style={{
                        position: 'absolute',
                        opacity: 0,
                        width: '100%',
                        height: '100%',
                        cursor: 'pointer',
                        top: 0,
                        left: 0,
                        zIndex: 1
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => document.getElementById('image-upload-input').click()}
                    style={{
                      marginTop: '12px',
                      background: 'linear-gradient(135deg, #608fd3 0%, #4a7bc4 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '8px 16px',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      width: '100%'
                    }}
                  >
                    {t('company.editProfile.changeImage') || 'تغيير الصورة'}
                  </button>
                </div>
              ) : (
                /* Image Upload Area (when no image) */
                <div
                  className="image-upload-area"
                  style={{
                    position: 'relative',
                    border: '2px dashed #d1d5db',
                    borderRadius: '12px',
                    padding: '24px',
                    background: '#fafafa',
                    textAlign: 'center',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                >
                  <input
                    id="image-upload-input"
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    style={{
                      position: 'absolute',
                      opacity: 0,
                      width: '100%',
                      height: '100%',
                      cursor: 'pointer',
                      top: 0,
                      left: 0
                    }}
                  />

                  <div>
                    <div style={{
                      width: '64px',
                      height: '64px',
                      background: 'linear-gradient(135deg, #608fd3 0%, #4a7bc4 100%)',
                      borderRadius: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 16px',
                      boxShadow: '0 4px 12px rgba(76, 116, 179, 0.25)'
                    }}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                        <circle cx="8.5" cy="8.5" r="1.5"></circle>
                        <polyline points="21 15 16 10 5 21"></polyline>
                      </svg>
                    </div>
                    <div style={{ fontWeight: '600', color: '#111', marginBottom: '8px', fontSize: '16px' }}>
                      {t('company.editProfile.uploadImage') || 'رفع صورة الشركة'}
                    </div>
                    <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>
                      {t('company.editProfile.dragDropImage') || 'انقر لاختيار صورة أو اسحبها هنا'}
                    </div>
                    <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                      {t('company.editProfile.imageHint') || 'ملفات الصور فقط (حد أقصى 2MB)'}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="grid-2">
              <div className="form-group-modern">
                <label className="form-label">{t('company.editProfile.name') || 'اسم الشركة'}</label>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  className="form-control-modern"
                  placeholder={t('company.editProfile.name') || 'اسم الشركة'}
                  required
                />
              </div>
              <div className="form-group-modern">
                <label className="form-label">{t('company.editProfile.email') || 'البريد الإلكتروني'}</label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  className="form-control-modern"
                  placeholder="email@company.com"
                />
              </div>
              <div className="form-group-modern">
                <label className="form-label">{t('company.editProfile.address') || 'العنوان'}</label>
                <input
                  type="text"
                  name="address"
                  value={form.address}
                  onChange={handleChange}
                  className="form-control-modern"
                  placeholder={t('company.editProfile.address') || 'العنوان'}
                />
              </div>
              <div className="form-group-modern">
                <label className="form-label">{t('company.editProfile.website') || 'الموقع الإلكتروني'}</label>
                <input
                  type="url"
                  name="site_link"
                  value={form.site_link}
                  onChange={handleChange}
                  className="form-control-modern"
                  placeholder="https://example.com"
                />
              </div>
              <div className="form-group-modern grid-span-2">
                <label className="form-label">{t('company.editProfile.bio') || 'نبذة عن الشركة'}</label>
                <textarea
                  name="bio"
                  value={form.bio}
                  onChange={handleChange}
                  className="form-control-modern"
                  placeholder={t('company.editProfile.bioPlaceholder') || 'وصف مختصر عن الشركة'}
                  rows={7}
                />
              </div>
            </div>

            <div className="actions">
              <button
                type="submit"
                className="btn-primary-modern w-100"
                disabled={saving}
              >
                {saving ? (t('company.editProfile.saving') || 'جارٍ الحفظ...') : (t('company.editProfile.save') || 'حفظ')}
              </button>
            </div>
          </form>
        </div>

        {/* Local styles */}
        <style>
          {`
            .edit-hero {
              background: linear-gradient(135deg, rgba(96,143,211,0.15) 0%, rgba(74,123,196,0.15) 100%);
              border-radius: 18px;
              padding: 18px;
            }
            .edit-hero-inner {
              display: flex;
              align-items: center;
              gap: 12px;
            }
            .edit-hero-badge {
              width: 44px;
              height: 44px;
              border-radius: 12px;
              display: grid;
              place-items: center;
              background: linear-gradient(135deg, #608fd3 0%, #4a7bc4 100%);
              color: #fff;
              font-size: 22px;
              box-shadow: 0 10px 20px rgba(76, 116, 179, 0.25);
            }
            .edit-hero-title {
              font-weight: 800;
              font-size: 1.1rem;
            }
            .edit-hero-subtitle {
              color: #475569;
              font-size: 0.9rem;
            }
            .edit-card {
              background: #ffffff;
              border-radius: 16px;
              padding: 18px;
              box-shadow: 0 10px 30px rgba(0,0,0,0.05);
              border: 1px solid #eef2f7;
            }
            .grid-2 {
              display: grid;
              grid-template-columns: 1fr;
              gap: 14px;
            }
            @media (min-width: 768px) {
              .grid-2 {
                grid-template-columns: 1fr 1fr;
              }
              .grid-span-2 {
                grid-column: span 2;
              }
            }
            .form-group-modern {
              display: flex;
              flex-direction: column;
              gap: 6px;
            }
            .form-label {
              font-weight: 600;
              color: #111;
              font-size: 0.95rem;
              margin-bottom: 4px;
            }
            .form-control-modern {
              width: 100%;
              border: 1px solid #e5e7eb;
              background: #fafafa;
              padding: 12px 14px;
              border-radius: 12px;
              outline: none;
              transition: box-shadow 0.15s ease, border-color 0.15s ease, background-color 0.15s ease;
              font-size: 1rem;
            }
            .form-control-modern:focus {
              border-color: #4a7bc4;
              background: #fff;
              box-shadow: 0 0 0 4px rgba(74, 123, 196, 0.15);
            }
            .actions {
              display: flex;
              justify-content: flex-start;
              margin-top: 12px;
            }
            .btn-primary-modern {
              background: linear-gradient(135deg, #608fd3 0%, #4a7bc4 100%);
              color: #fff;
              border: none;
              border-radius: 12px;
              padding: 12px 20px;
              font-weight: 700;
              cursor: pointer;
              box-shadow: 0 10px 20px rgba(76, 116, 179, 0.25);
              font-size: 1rem;
            }
            .btn-primary-modern:disabled {
              opacity: 0.75;
              cursor: not-allowed;
              box-shadow: none;
            }
            .image-preview-card {
              margin-bottom: 16px;
            }
            .image-upload-area:hover {
              border-color: #4a7bc4;
              background: linear-gradient(135deg, #f0f7ff 0%, #e8f2ff 100%);
            }
          `}
        </style>
      </div>

      <RecruitmentCompanyBottomNavigation />
    </div>
  );
};

export default RecruitmentCompanyEditProfile;

